'use client';

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Voice-print waveform shown in the hero. Pure SVG, no canvas — server-friendly
 * and tiny. The shape is generated client-side after mount so each visitor sees a
 * subtly different "fingerprint", reinforcing the lab's biometric narrative.
 */
export function AnimatedWaveform({ className }: { className?: string }) {
  const [seed, setSeed] = useState(0.42);

  useEffect(() => {
    setSeed(Math.random());
  }, []);

  const samples = 96;
  const width = 800;
  const height = 240;
  const midY = height / 2;

  const points = Array.from({ length: samples }, (_, i) => {
    const x = (i / (samples - 1)) * width;
    const t = i / samples;
    // Mix three frequencies so the curve looks like real speech, not a sine.
    const wave =
      Math.sin(t * Math.PI * 2 * 4 + seed * 12) * 0.55 +
      Math.sin(t * Math.PI * 2 * 9 + seed * 7) * 0.25 +
      Math.sin(t * Math.PI * 2 * 17 + seed * 3) * 0.18;
    // Envelope so the wave fades at both edges (like a recorded clip).
    const envelope = Math.sin(t * Math.PI);
    const y = midY + wave * envelope * 90;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  // Vertical bars below the curve — frequency analyzer simulation.
  const bars = Array.from({ length: 48 }, (_, i) => {
    const t = i / 48;
    const envelope = Math.sin(t * Math.PI);
    const h = (Math.sin(t * Math.PI * 2 * 6 + seed * 9) * 0.5 + 0.5) * envelope * 90 + 8;
    const x = (i / 48) * width + width / 96;
    return { x, h };
  });

  return (
    <div className={cn('relative w-full overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm', className)}>
      {/* Spectrogram style backdrop */}
      <div className="absolute inset-0 spectrogram-bg opacity-60" />

      {/* Floating accent orb behind the wave */}
      <div className="orb left-[-20%] top-[-40%] h-72 w-72 bg-accent/40" style={{ animationDelay: '0s' }} />
      <div className="orb right-[-15%] bottom-[-30%] h-64 w-64 bg-primary/40" style={{ animationDelay: '6s' }} />

      <svg
        viewBox={`0 0 ${width} ${height}`}
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Forma de onda de voz"
        className="relative block h-auto w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="wave-stroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="hsl(173 70% 55%)" stopOpacity="0.15" />
            <stop offset="50%" stopColor="hsl(173 80% 60%)" stopOpacity="1" />
            <stop offset="100%" stopColor="hsl(173 70% 55%)" stopOpacity="0.15" />
          </linearGradient>
          <linearGradient id="wave-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(173 80% 60%)" stopOpacity="0.4" />
            <stop offset="100%" stopColor="hsl(173 80% 60%)" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="bar-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(173 80% 60%)" stopOpacity="0.95" />
            <stop offset="100%" stopColor="hsl(224 60% 45%)" stopOpacity="0.4" />
          </linearGradient>
        </defs>

        {/* center axis line */}
        <line x1="0" y1={midY} x2={width} y2={midY} stroke="hsl(173 50% 60% / 0.18)" strokeWidth="1" strokeDasharray="3 5" />

        {/* filled area under the wave */}
        <polygon
          points={`0,${midY} ${points} ${width},${midY}`}
          fill="url(#wave-fill)"
          opacity="0.7"
        />

        {/* main wave curve */}
        <polyline
          points={points}
          fill="none"
          stroke="url(#wave-stroke)"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="animate-pulse-soft"
        />

        {/* frequency analyzer bars */}
        <g>
          {bars.map((b, i) => (
            <rect
              key={i}
              x={b.x}
              y={height - 30 - b.h}
              width="6"
              height={b.h}
              rx="1.5"
              fill="url(#bar-fill)"
              className="origin-bottom"
              style={{
                transformOrigin: `${b.x + 3}px ${height - 30}px`,
                animation: `wave-bars ${1.4 + (i % 5) * 0.12}s ease-in-out ${i * 0.04}s infinite`,
              }}
            />
          ))}
        </g>

        {/* timestamp ticks at the bottom */}
        <g fill="hsl(173 50% 60% / 0.5)" fontSize="9" fontFamily="monospace">
          {[0, 0.25, 0.5, 0.75, 1].map((t) => (
            <text key={t} x={t * width} y={height - 8} textAnchor={t === 0 ? 'start' : t === 1 ? 'end' : 'middle'}>
              {`${(t * 3.2).toFixed(1)}s`}
            </text>
          ))}
        </g>
      </svg>
    </div>
  );
}
